<script lang="ts">
	import imgAgile from "@assets/images/development/agile.png?h=445&webp&imagetools";
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import LinkCard from "@components/LinkCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import TechnologyCards from "@components/TechnologyCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getDevelopmentServices } from "../data/getDevelopmentServices.js";
	import { getDevelopmentStepByStep } from "../data/getDevelopmentStepByStep.js";
	import { getFaqsDevelopment } from "../data/getFaqsDevelopment.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { ReferencedClients } from "../data/getReferences.js";
	import { getTechnologies } from "../data/getTechnologies.js";

	const { tenant } = getGlobalTemplateProps();
	const services = getDevelopmentServices(tenant);
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Objevte cg[[možnosti]] vývoje na míru" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Potřebuje aplikaci či komplexní systém „na klíč“, ale nemáte zatím připravené zadání a další detaily? sp[[Poradíme si s tím.]]"
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Nezávazná konzultace" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" gridChildren="container-12 w-full" centered>
		{#snippet headline()}
			<Formatted text="S čím jsme vám k ruce v rámci cg[[zakázkového vývoje]]" />
		{/snippet}
		<div
			class="max-w-[75rem] grid justify-items-center sm:justify-items-stretch grid-cols-1 min-[370px]:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-[1.03rem] mb-12 mx-auto"
		>
			{#each services.filter(({ show }) => show === true) as { title, iconName, href }, index (index)}
				<LinkCard {title} {iconName} {href} />
			{/each}
		</div>
		<Link
			variant={ButtonVariant.Phlox}
			class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
		>
			<Formatted text="Chci fastest služby" />
			<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
		</Link>
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Náš recept na hladký průběh? cg[[Agilní vývoj]]" />
			<!-- Visible on xl -->
			<img src={imgAgile} alt="Agilní vývoj" width="462" height="326" class="hidden xl:block xl:mt-40" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Pouze 1 % našich klientů na začátku přijde s kompletní zadávací dokumentaci, má přesně definovaný rozpočet nebo rozsah zakázky a další důležité detaily. Proto se sp[[nejčastěji vydáváme cestou agilního vývoje.]]"
				/>
			</p>

			<p>
				<Formatted
					text="Jedná se o cyklus složený z tzv. vývojových sprintů, díky kterému projekt rozdělíme na menší fáze. Úlohy evidujeme v interním systému a od začátku do konce mají všichni zúčastnění přehled o zadání, odpovědných osobách i časových odhadech. Jednotlivými sprinty agilního vývoje sp[[vás postupně a přehledně provedeme.]]"
				/>
			</p>

			<p>
				<Formatted
					text="Preferujeme sp[[přístup MVP]] (Minimum viable product – produkt, který splňuje primární cíle, je spustitelný a životaschopný. Na základě MVP můžeme produkt dále rozvíjet). spuštění minimálně použitelného produktu), umožňující získání zpětné vazby od zákazníků a sbírání dat pro další vývoj."
				/>
			</p>
			<!-- Visible up to lg -->
			<img src={imgAgile} alt="Agilní vývoj" width="462" height="326" class="block xl:hidden mt-8 mx-auto" />
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="cg[[Pomohli]] jsme cukrárně i nahrávacímu studiu cg[[v cloudu]]" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.CustomDevelopment} />
		</div>
		<div class="flex flex-col mt-8 lg:mt-12 gap-3">
			<Link
				variant={ButtonVariant.GreenOutlineLightBg}
				class="mx-auto flex items-center justify-center"
				href={getReferencePath(tenant)}
			>
				Zobrazit více
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	<Section
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		background={Background.LightGradient}
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="cg[[Technologie]], které používáme" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Pracujeme s ověřenými a dlouhodobě podporovanými technologiemi, které nabízí ideální poměr cena/výkon. Custom software development děláme primárně sp[[na cloudu AWS (Amazon Web Services).]]"
				/>
			</p>
		{/snippet}
	</Section>
	<TechnologyCards technologies={getTechnologies(tenant)} />
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Chci fastest služby
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proces]] vývoje na míru" />
			<span class="block text-2md font-bold mt-6">
				<Formatted text="Vývoj každého software má unikátní příběh. Obecně ale postupujeme v cg[[6 krocích.]]" />
			</span>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getDevelopmentStepByStep(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k cg[[vývoji na míru]]" />
		{/snippet}
		<FaqItems faqs={getFaqsDevelopment(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
